import { useEffect, useMemo, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";

/**
 * Subscribe to a single measurement result document:
 * measurements/{uid}/{variant}/{docId}
 *
 * - If the doc doesn't exist yet (e.g. backend processing), `exists=false` and `data=null`.
 * - Stays subscribed, so the UI updates as soon as it becomes available.
 */
export function useMeasurementResultDoc(db, uid, variant, docId) {
  const [data, setData] = useState(null);
  const [exists, setExists] = useState(false);
  const [loading, setLoading] = useState(!!db && !!uid && !!variant && !!docId);
  const [error, setError] = useState(null);
  const [metadata, setMetadata] = useState({
    fromCache: false,
    hasPendingWrites: false,
  });

  const ref = useMemo(() => {
    if (!db || !uid || !variant || !docId) return null;
    return doc(db, "measurements", uid, variant, docId);
  }, [db, uid, variant, docId]);

  useEffect(() => {
    if (!ref) {
      setData(null);
      setExists(false);
      setLoading(false);
      setError(null);
      setMetadata({ fromCache: false, hasPendingWrites: false });
      return;
    }

    setLoading(true);
    setError(null);
    setData(null);
    setExists(false);
    setMetadata({ fromCache: false, hasPendingWrites: false });

    const unsub = onSnapshot(
      ref,
      { includeMetadataChanges: true },
      (snap) => {
        setExists(snap.exists());
        setData(snap.exists() ? snap.data() : null);
        setMetadata({
          fromCache: snap.metadata.fromCache,
          hasPendingWrites: snap.metadata.hasPendingWrites,
        });
        setLoading(false);
      },
      (err) => {
        setError(err);
        setLoading(false);
      },
    );

    return () => unsub();
  }, [ref]);

  return { data, exists, loading, error, metadata };
}
